import { z } from 'zod';

/**
 * S-FIX-1-BE.B.1 — `AuthScope` — extended `/auth/me` response.
 *
 * Содержит:
 *   - warehouse_codes: коды складов в scope user-а (пустой массив если unrestricted=true)
 *   - unrestricted: true для admin/manager (видят все warehouse)
 *   - abilities: мапа entity → actions[] (для FE disabled-state без 403-roundtrip)
 *
 * FE использует через `useUserScope()` hook (FE.B.1).
 */

export const AuthAction = z.enum(['read', 'create', 'update', 'delete', 'approve']);
export type AuthAction = z.infer<typeof AuthAction>;

/**
 * P3.1 / P17.2 — Project-domain abilities (in addition to per-entity actions).
 * Used by FE `useAbility(name)` helper (G14.6) для CTA gating. BE side
 * gating обеспечен через role-based middleware checks; этот enum документирует
 * canonical strings.
 */
export const ProjectAbility = z.enum([
  'sales_order:read',
  'sales_order:write',
  'sales_order:transition',
  'sales_order:cancel',
  'project:read',
  'project:write',
  'project:transition',
  'project:approve',
  'task:read',
  'task:write',
  'task:reorder',
  'task:dependency_manage',
  'milestone:read',
  'milestone:write',
  'milestone:reach',
  'milestone:draft_invoice',
  'task_log:read',
  'task_log:write',
  'user:write',
]);
export type ProjectAbility = z.infer<typeof ProjectAbility>;

// B6 invariant — entity-уровень response-схемы forward-compat (см. zod-strict.test.ts).
// `approver_hints` (UX-fix B6) — ability → role mapping для FE tooltips «approver: …»
// в `<DisabledWithReason>` обёртке (UX18). Empty record → graceful fallback.
export const AuthScope = z.object({
  warehouse_codes: z.array(z.string()),
  unrestricted: z.boolean(),
  abilities: z.record(z.array(AuthAction)),
  approver_hints: z.record(z.string()).optional(),
});

export type AuthScope = z.infer<typeof AuthScope>;
